import React, { useCallback, useMemo, useState } from 'react'
import cx from 'classnames'
import { any, arrayOf, bool, func, node, number, objectOf, string } from 'prop-types'
import { DropTarget } from 'react-dnd'

import { Button, Highlight } from '@wf-common/atoms'

import VirtualList from '../virtual-list/virtual-list'
import virtualListPropType from '../virtual-list/virtual-list-prop-type'
import { filterListItems, compareStringsWithLeftTrimAndLowerCase } from './assignment-editor-helper'
import { AssignmentEditorListFilter } from './assignment-editor-list-filter'
import { AssignmentEditorListRowConnected } from './assignment-editor-list-row'
import { RemoveHint } from './remove-hint'

import styles from './assignment-editor-list.module.css'

const propTypes = {
    actionIcon: node,
    allItems: arrayOf(objectOf(any)).isRequired,
    canDrop: bool,
    className: string,
    connectDropTarget: func,
    createListItemIcon: func,
    customItemSort: func,
    emptyPlaceholder: node,
    enableItemForUnassignment: func,
    filterBarClassName: string,
    filterBy: string,
    globalAction: func.isRequired,
    globalActionText: string,
    groups: arrayOf(objectOf(any)),
    groupsFilterClassName: string,
    groupsFilterIcon: node,
    groupsPlaceholder: string,
    header: string.isRequired,
    isOver: bool,
    isOverCurrent: bool,
    listClassName: string,
    listId: number.isRequired,
    listItemIcon: node,
    noResultsPlaceholder: node,
    onChange: func,
    onClickActionIcon: func,
    onMoveItem: func,
    placeholder: string,
    removeHintText: string,
    searchFilterClassName: string,
    showDropOverlay: bool,
    virtual: virtualListPropType
}

const defaultProps = {
    enableItemForUnassignment: () => true,
    groups: [],
    onChange: () => null,
    showDropOverlay: false
}

const cardTarget = {
    canDrop(props, monitor) {
        const sourceListId = monitor.getItem().listId
        return sourceListId !== props.id
    },
    drop(props, monitor) {
        const { id } = props
        const sourceObj = monitor.getItem()
        if (id !== sourceObj.listId) {
            sourceObj.action(sourceObj.item)
        }
        return {
            listId: id
        }
    }
}

const RENDER_VIRTUAL_MIN_LENGTH = 20

const AssignmentEditorList = ({
    actionIcon,
    allItems,
    canDrop,
    className,
    connectDropTarget,
    createListItemIcon,
    customItemSort,
    emptyPlaceholder,
    enableItemForUnassignment,
    filterBarClassName,
    filterBy,
    globalAction,
    globalActionText,
    groups,
    groupsFilterClassName,
    groupsFilterIcon,
    groupsPlaceholder,
    header,
    isOver,
    listClassName,
    listId,
    listItemIcon,
    noResultsPlaceholder,
    onChange,
    onClickActionIcon,
    onMoveItem,
    placeholder,
    removeHintText,
    searchFilterClassName,
    showDropOverlay,
    virtual
}) => {
    const allItemIds = allItems.map(item => item?.id)

    const filterClassName = cx(styles['assignment-editor-list'], className)
    const subhead2ClassName = cx(styles['subhead2'])
    const titlewrapperClassName = cx(styles['title-wrapper'])

    const [filterText, setFilterText] = useState('')
    const [filterGroup, setFilterGroup] = useState(null)

    /**
     * filter available items from given items by group.
     */
    const group = groups ? groups.find(obj => obj.id === filterGroup) : null
    const items = useMemo(
        () =>
            group
                ? group.items.reduce((acc, item) => {
                      const matchedItem = allItems.find(filterItem => filterItem.id === item.id)
                      if (matchedItem) {
                          acc.push(matchedItem)
                      }
                      return acc
                  }, [])
                : allItems,
        [allItems, group]
    )

    const filterValue = filterText.trim().toLowerCase()
    const filteredItems = useMemo(
        () => items.filter(item => filterListItems(filterValue, item, filterBy)),
        [filterValue, items, filterBy]
    )

    const listPlaceholder = filterText.length ? noResultsPlaceholder : emptyPlaceholder

    /**
     * Highlights the filterText in given item.
     * @param {string} highlightText - FilterText
     * @param {object} child - option that gets highlighted
     * @return {object} - highlighted option
     */
    const highlight = useCallback(
        (highlightText = '', item, index) => {
            const filterText = highlightText.trim().toLowerCase()

            if (!item) {
                return null
            }

            const { appendix, name } = item
            const listItemIconElement = createListItemIcon ? createListItemIcon(item) : listItemIcon
            const renderItemProps = {
                actionIcon,
                appendix,
                icon: listItemIconElement,
                index,
                item,
                listId,
                moveCard: onMoveItem,
                name,
                onClick: onClickActionIcon
            }

            if (filterText) {
                if (filterBy === 'all') {
                    // when should not be filtered by special field
                    renderItemProps.name = (
                        <Highlight keyword={filterText} text={name} highlightClassName={styles['highlighted-text']} />
                    )
                    renderItemProps.appendix = (
                        <Highlight
                            keyword={filterText}
                            text={appendix}
                            highlightClassName={styles['highlighted-text']}
                        />
                    )
                } else {
                    // When a special filterBy option is given or NO appendix
                    const filterOption = (filterBy !== 'all' && filterBy) || 'name'
                    const text = item?.[filterOption]

                    renderItemProps[filterOption] = (
                        <Highlight keyword={filterText} text={text} highlightClassName={styles['highlighted-text']} />
                    )
                }
            }

            return <AssignmentEditorListRowConnected key={item.id} {...renderItemProps} />
        },
        [actionIcon, filterBy, listId, onClickActionIcon, createListItemIcon, listItemIcon, onMoveItem]
    )

    const filteredChildren = useMemo(
        () =>
            filteredItems
                .sort((item1, item2) =>
                    customItemSort
                        ? customItemSort(item1, item2)
                        : compareStringsWithLeftTrimAndLowerCase(item1.name, item2.name)
                )
                .map((item, index) => {
                    const highlighted = highlight(filterValue, item, index)
                    return enableItemForUnassignment(item) ? (
                        highlighted
                    ) : (
                        <div
                            className={cx(styles['disabled-row'], 't3sel-assignment-editor-list-row-disabled')}
                            key={item.id}
                        >
                            {highlighted}
                        </div>
                    )
                }),
        [filteredItems, filterValue, customItemSort, enableItemForUnassignment, highlight]
    )

    const shouldRenderVirtual = virtual && filteredChildren.length > RENDER_VIRTUAL_MIN_LENGTH

    const childrenList = shouldRenderVirtual ? (
        <VirtualList settings={virtual}>{filteredChildren}</VirtualList>
    ) : (
        filteredChildren
    )

    const filterListClassName = cx(styles['list'], listClassName, {
        [styles['virtual-list']]: shouldRenderVirtual
    })

    const unremovableItems = useMemo(
        () => items.filter(item => !enableItemForUnassignment(item)),
        [items, enableItemForUnassignment]
    )

    const onChangeGroup = useCallback(
        value => {
            onChange(value)
            setFilterGroup(value)
        },
        [onChange]
    )

    return (
        <div className={filterClassName}>
            <div className={subhead2ClassName}>
                <div className={titlewrapperClassName}>{header}</div>
            </div>
            <AssignmentEditorListFilter
                {...{
                    allItemIds,
                    filterBarClassName,
                    filterGroup,
                    filterText,
                    groups,
                    groupsFilterClassName,
                    groupsFilterIcon,
                    groupsPlaceholder,
                    onChangeFilterText: setFilterText,
                    onChangeGroup,
                    placeholder,
                    searchFilterClassName
                }}
            />
            {connectDropTarget(
                <div className={filterListClassName}>
                    {isOver && showDropOverlay ? <span className={styles['drop-overlay']} /> : null}
                    {canDrop && isOver ? <span className={styles['drop-placeholder']} /> : null}
                    {filteredItems.length === 0 ? listPlaceholder : null}
                    {childrenList}
                </div>
            )}
            <Button
                onClick={() => globalAction(filteredItems)}
                disabled={!(items.length - unremovableItems.length)}
                className="t3sel-assignment-editor-list-button"
            >
                {globalActionText} ({filteredItems.length - unremovableItems.length})
            </Button>

            {unremovableItems.length && removeHintText ? (
                <RemoveHint text={removeHintText} className={styles['remove-hint']} />
            ) : null}
        </div>
    )
}

AssignmentEditorList.propTypes = propTypes
AssignmentEditorList.defaultProps = defaultProps

export default DropTarget('CARD', cardTarget, (connect, monitor) => ({
    canDrop: monitor.canDrop(),
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver(),
    isOverCurrent: monitor.isOver({ shallow: true })
}))(AssignmentEditorList)
